<script setup lang="ts">
    import { onMounted, inject, reactive, computed } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SubscriptionAccess, getSmsTopUpCredit } from '@views/BackOffice/Subscription/Subscription.access';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps, SwitchStyle, TextOrHtmlTextType } from '@shared/components/Form/Form';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import { SmsTopUpCreditStatus } from '@views/Account/AccountPrePaidBalance.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    // The SMS top-up on invoice fields — trust, limit, credit status — as one group, its own row in the form.
    const SMS_TOP_UP_GROUP = 'SMS Top-up on Invoice';
    const CREDIT_FIELDS = ['SmsTopUpInvoiceLimit', 'SmsTopUpCreditUsed', 'SmsTopUpCreditAvailable', 'SmsTopUpCreditBlocked'];

    const state = reactive<{
        companyForm: FormProps;
        companyFormReady: boolean;
    }>({
        companyForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        companyFormReady: false,
    });

    onMounted(async () => {
        setupCompanyFormData();

        await subscriptionAccess.getIndustries().then(() => {
            setIndustryOnCompanyFormData();
        });

        await subscriptionAccess.getCountries().then(() => {
            setCountryOnCompanyFormData();
        });
    });

    function setupCompanyFormData(): void {
        state.companyForm.data = {
            PaymentPeriod: {
                modelValue: paymentPeriodLabel.value,
                group: 'Billing Cycle & Method',
                required: false,
                textOrHtmlText: {
                    label: $Resources.BillingPeriodLabel,
                },
            },
            PaymentMethod: {
                modelValue: paymentMethodLabel.value,
                group: 'Billing Cycle & Method',
                required: false,
                textOrHtmlText: {
                    label: $Resources.BillingMethodLabel,
                },
            },
            // Card 5.3 (7c): "Lägg på faktura" on the SMS top-up only for a company trusted here, whatever its payment method.
            SmsTopUpInvoiceAllowed: {
                modelValue: subscriptionAccess.state.boClientCompany!.SmsTopUpInvoiceAllowed,
                group: SMS_TOP_UP_GROUP,
                required: false,
                checkbox: {
                    label: $Resources.BackOfficeSmsTopUpInvoiceAllowedLabel,
                    switchStyle: SwitchStyle.Standard,
                },
            },
            // SMS top-up on invoice (R7): required for "Lägg på faktura" — no limit, no invoice. Excl. VAT, the subscription's currency.
            // Shown only while the switch is on (onFieldChanged).
            SmsTopUpInvoiceLimit: {
                modelValue: subscriptionAccess.state.boClientCompany!.SmsTopUpInvoiceLimit,
                group: SMS_TOP_UP_GROUP,
                required: false,
                show: subscriptionAccess.state.boClientCompany!.SmsTopUpInvoiceAllowed,
                input: {
                    // The fallback until the key is imported into SimpleLocalize.
                    label: $Resources.BackOfficeSmsTopUpInvoiceLimitLabel ?? 'Credit limit, SMS top-up on invoice (excl. VAT)',
                    type: 'number',
                },
            },
            // The credit status (decided 2026-10-28): read live from Chargebee, read only. The value is the placeholder, not the
            // model value, so reading it never marks the form as changed (the Save button follows the model values).
            ...creditField('SmsTopUpCreditUsed', $Resources.BackOfficeSmsTopUpCreditUsedLabel ?? 'Used (excl. VAT)'),
            ...creditField('SmsTopUpCreditAvailable', $Resources.BackOfficeSmsTopUpCreditAvailableLabel ?? 'Left (excl. VAT)'),
            ...creditField('SmsTopUpCreditBlocked', $Resources.BackOfficeSmsTopUpCreditBlockedLabel ?? 'Blocked for top-ups on invoice', true),
            Name: {
                modelValue: subscriptionAccess.state.boClientCompany!.Name,
                group: 'Company info',
                required: true,
                input: {
                    label: 'Name',
                },
            },
            IndustryId: {
                modelValue: subscriptionAccess.state.boClientCompany!.IndustryId,
                group: 'Company info',
                required: false,
                select: {
                    label: 'Industry',
                    options: [],
                    readonly: true,
                },
            },
            BillingName: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingName,
                group: 'Billing info',
                required: false,
                input: {
                    label: `Företagsnamn (om annat än ${subscriptionAccess.state.boClientCompany!.Name})`,
                },
            },
            BillingRecipientName: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingRecipientName,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Namn på faktura',
                },
            },
            BillingEmail: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingEmail,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Billing Email',
                    type: 'email',
                },
            },
            EInvoiceAddress: {
                modelValue: subscriptionAccess.state.boClientCompany!.EInvoiceAddress,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'E-Invoice Address',
                },
            },
            InvoicingInstructions: {
                modelValue: subscriptionAccess.state.boClientCompany!.InvoicingInstructions,
                group: 'Billing info',
                required: false,
                textArea: {
                    label: 'Invoicing instructions',
                },
            },
            BillingAddress: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAddress,
                group: 'Billing info',
                required: false,
                textArea: {
                    label: 'Billing Address (replaced by detailed fields)',
                },
            },
            BillingAttention: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAttention,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Attention',
                },
            },
            BillingAddress1: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAddress1,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Address1',
                },
            },
            BillingAddress2: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAddress2,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Address2',
                },
            },
            BillingZipCode: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingZipCode,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'ZipCode',
                },
            },
            BillingCity: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingCity,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'City',
                },
            },
            BillingCountry: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingCountry,
                group: 'Billing info',
                required: true,
                select: {
                    label: 'Country',
                    options: [],
                    readonly: true,
                },
            },
            Url: {
                modelValue: subscriptionAccess.state.boClientCompany!.Url,
                group: 'Company info',
                required: false,
                input: {
                    label: 'URL',
                    type: 'url',
                },
            },
            BusinessId: {
                modelValue: subscriptionAccess.state.boClientCompany!.BusinessId,
                group: 'Company info',
                required: false,
                input: {
                    label: 'Business ID',
                    placeholder: 'Ange organisationsnummer eller motsvarande',
                },
            },
            VatNumber: {
                modelValue: subscriptionAccess.state.boClientCompany!.VatNumber,
                group: 'Company info',
                required: false,
                input: {
                    label: 'VAT Number',
                    placeholder: 'Ange momsregistreringsnummer om företaget är i ett EU-land',
                },
            },
            IsEuCompany: {
                modelValue: subscriptionAccess.state.boClientCompany!.IsEuCompany,
                group: 'Company info',
                required: false,
                checkbox: {
                    label: 'Is EU Company',
                },
            },
            Contact: {
                modelValue: subscriptionAccess.state.boClientCompany!.Contact,
                group: 'Contract contact',
                required: false,
                input: {
                    label: 'Contract contact',
                },
            },
            Email: {
                modelValue: subscriptionAccess.state.boClientCompany!.Email,
                group: 'Contract contact',
                required: false,
                input: {
                    label: 'Contract email',
                    type: 'email',
                },
            },
            Phone: {
                modelValue: subscriptionAccess.state.boClientCompany!.Phone,
                group: 'Contract contact',
                required: false,
                input: {
                    label: 'Contract phone',
                    type: 'tel',
                },
            },
            BillingContactName: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingContactName,
                group: 'Billing contact',
                required: false,
                input: {
                    label: 'Billing Contact Name',
                },
            },
            BillingContactEmail: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingContactEmail,
                group: 'Billing contact',
                required: false,
                input: {
                    label: 'Billing Contact Email',
                    type: 'email',
                },
            },
            BillingContactPhone: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingContactPhone,
                group: 'Billing contact',
                required: false,
                input: {
                    label: 'Billing Contact Phone',
                    type: 'tel',
                },
            },
            SupportContactName: {
                modelValue: subscriptionAccess.state.boClientCompany!.SupportContactName,
                group: 'Support contact',
                required: false,
                input: {
                    label: 'Support Contact Name',
                },
            },
            SupportContactEmail: {
                modelValue: subscriptionAccess.state.boClientCompany!.SupportContactEmail,
                group: 'Support contact',
                required: false,
                input: {
                    label: 'Support Contact Email',
                    type: 'email',
                },
            },
            SupportContactPhone: {
                modelValue: subscriptionAccess.state.boClientCompany!.SupportContactPhone,
                group: 'Support contact',
                required: false,
                input: {
                    label: 'Support Contact Phone',
                    type: 'tel',
                },
            },
            PasswordExpireDays: {
                modelValue: subscriptionAccess.state.boClientCompany!.PasswordExpireDays,
                group: 'Security',
                required: false,
                input: {
                    label: 'Password Expire Days',
                    type: 'number',
                },
            },
        };

        state.companyFormReady = true;
        if (subscriptionAccess.state.boClientCompany!.SmsTopUpInvoiceAllowed) loadCredit();
    }

    function setIndustryOnCompanyFormData(): void {
        let industriesAsOptions = subscriptionAccess.state.industries.map((industry) => {
            return { value: industry.IndustryId, label: industry.Name };
        });

        state.companyForm.data.IndustryId.select!.options = industriesAsOptions;
    }

    function setCountryOnCompanyFormData(): void {
        let countriesAsOptions = subscriptionAccess.state.countries.map((country) => {
            return { value: country, label: country };
        });

        state.companyForm.data.BillingCountry.select!.options = countriesAsOptions;
    }

    const paymentPeriodLabel = computed(() => {
        if (!subscriptionAccess.state.boServiceSubscription) return '';

        return `${subscriptionAccess.state.boServiceSubscription.PaymentPeriod} ${$Resources.RecurrenceLabelMonths}`;
    });

    const paymentMethodLabel = computed(() => {
        switch (subscriptionAccess.state.boServiceSubscription?.DefaultPaymentMethod) {
            case 1:
                return 'Credit card';
            case 2:
                return 'Invoice';
            case 3:
                return 'Paper invoice';
            case 4:
                return 'E-invoice';
            default:
                return '';
        }
    });

    async function saveCompany() {
        if (!state.companyFormReady) return;

        state.companyForm.setInitData = false;

        const company = subscriptionAccess.state.boClientCompany!;
        const companyData = state.companyForm.data;

        company.Name = companyData.Name.modelValue;
        company.IndustryId = companyData.IndustryId.modelValue;
        company.BillingName = companyData.BillingName.modelValue;
        company.BillingRecipientName = companyData.BillingRecipientName.modelValue;
        company.BillingAddress = companyData.BillingAddress.modelValue;
        company.BillingAttention = companyData.BillingAttention.modelValue;
        company.BillingAddress1 = companyData.BillingAddress1.modelValue;
        company.BillingAddress2 = companyData.BillingAddress2.modelValue;
        company.BillingZipCode = companyData.BillingZipCode.modelValue;
        company.BillingCity = companyData.BillingCity.modelValue;
        company.BillingCountry = companyData.BillingCountry.modelValue;
        company.BillingEmail = companyData.BillingEmail.modelValue;
        company.EInvoiceAddress = companyData.EInvoiceAddress.modelValue;
        company.InvoicingInstructions = companyData.InvoicingInstructions.modelValue;
        company.Url = companyData.Url.modelValue;
        company.BusinessId = companyData.BusinessId.modelValue;
        company.VatNumber = companyData.VatNumber.modelValue;
        company.IsEuCompany = companyData.IsEuCompany.modelValue;
        company.SmsTopUpInvoiceAllowed = companyData.SmsTopUpInvoiceAllowed.modelValue;
        // Blank is no limit (null), not 0.
        const limit = companyData.SmsTopUpInvoiceLimit.modelValue;
        company.SmsTopUpInvoiceLimit = limit === '' || limit == null ? null : Number(limit);
        company.Contact = companyData.Contact.modelValue;
        company.Email = companyData.Email.modelValue;
        company.Phone = companyData.Phone.modelValue;
        company.BillingContactName = companyData.BillingContactName.modelValue;
        company.BillingContactEmail = companyData.BillingContactEmail.modelValue;
        company.BillingContactPhone = companyData.BillingContactPhone.modelValue;
        company.SupportContactName = companyData.SupportContactName.modelValue;
        company.SupportContactEmail = companyData.SupportContactEmail.modelValue;
        company.SupportContactPhone = companyData.SupportContactPhone.modelValue;
        company.PasswordExpireDays = companyData.PasswordExpireDays.modelValue;

        const success = await subscriptionAccess.saveClientCompany(company);
        if (success) {
            state.companyForm.setInitData = true;
            // A saved trust or limit changes the credit status: read it again.
            if (company.SmsTopUpInvoiceAllowed) loadCredit();
        }
    }

    // The fallbacks hold until the keys are imported into SimpleLocalize.
    const t = (key: string, fallback: string): string => $Resources[key] ?? fallback;

    // html: the blocked field, shown as a yellow warning while blocked (setBlocked).
    function creditField(key: string, label: string, html = false) {
        return {
            [key]: {
                modelValue: '',
                group: SMS_TOP_UP_GROUP,
                required: false,
                show: subscriptionAccess.state.boClientCompany!.SmsTopUpInvoiceAllowed,
                textOrHtmlText: { label, placeholder: '…', type: html ? TextOrHtmlTextType.Html : TextOrHtmlTextType.Text },
            },
        };
    }

    function setCredit(key: string, text: string): void {
        state.companyForm.data[key].textOrHtmlText!.placeholder = text;
    }

    const escapeHtml = (text: string): string =>
        text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    // Blocked stands out as a warning, so support sees it at once; not blocked is plain text.
    function setBlocked(text: string, blocked: boolean): void {
        setCredit('SmsTopUpCreditBlocked', blocked ? `<div class="alert alert-warning m-0-b" role="status">${escapeHtml(text)}</div>` : escapeHtml(text));
    }

    async function loadCredit(): Promise<void> {
        let status: SmsTopUpCreditStatus;
        try {
            status = await getSmsTopUpCredit(subscriptionAccess.state.boClientCompany!.ClientCompanyId);
        } catch {
            const unavailable = t('BackOfficeSmsTopUpCreditUnavailable', 'Could not be read from Chargebee');
            setCredit('SmsTopUpCreditUsed', unavailable);
            setCredit('SmsTopUpCreditAvailable', unavailable);
            setBlocked(unavailable, true);
            return;
        }
        const money = (value: number | null) => (value == null ? '–' : formatMoney(value, status.CurrencyCode, 2));
        setCredit('SmsTopUpCreditUsed', money(status.Used));
        setCredit('SmsTopUpCreditAvailable', money(status.Available));
        setBlocked(blockedText(status), status.Blocked !== 'None');
    }

    function blockedText(status: SmsTopUpCreditStatus): string {
        switch (status.Blocked) {
            case 'None':
                return t('BackOfficeSmsTopUpCreditNotBlocked', 'No');
            case 'OverdueInvoice':
                return t('BackOfficeSmsTopUpCreditBlockedOverdue', 'Yes – an overdue invoice');
            case 'AboveInvoiceLimit':
                return t('BackOfficeSmsTopUpCreditBlockedLimit', 'Yes – the limit is reached');
            case 'SubscriptionNotInGoodStanding':
                return t('BackOfficeSmsTopUpCreditBlockedStanding', 'Yes – the subscription is payment failed, fraud or cancelled');
            case 'NotChargebeeOwned':
                return t('BackOfficeSmsTopUpCreditBlockedNotChargebee', 'Yes – not billed through Chargebee');
            case 'InvoiceDetailsMissing':
                return t('BackOfficeSmsTopUpCreditBlockedDetails', 'Yes – the billing address in Chargebee is incomplete');
            default:
                return t('BackOfficeSmsTopUpCreditBlockedNotTrusted', 'Yes – not trusted, no limit set or invoice top-ups switched off');
        }
    }

    function onFieldChanged(key: string, newVal: unknown): void {
        state.companyForm.data[key].modelValue = newVal;
        // The limit and the credit status only mean something for a company trusted with SMS top-ups on invoice.
        if (key === 'SmsTopUpInvoiceAllowed') {
            CREDIT_FIELDS.forEach((field) => (state.companyForm.data[field].show = !!newVal));
            if (newVal) loadCredit();
        }
    }
</script>

<template>
    <Form
        v-if="state.companyFormReady"
        class="fieldset-grid"
        :data="state.companyForm.data"
        :setInitData="state.companyForm.setInitData"
        @update:isValid="(key, newVal) => (state.companyForm!.data[key].isValid = newVal)"
        @update:modelValue="onFieldChanged"
        @onSubmit="saveCompany"></Form>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
